/**
 * Low-friction telemetry logger.
 *
 * Endpoint configuration:
 * - Set `VITE_LOG_ENDPOINT` to a full URL or relative path (e.g. `https://api.example.com/event` or `/event`).
 * - In dev mode, defaults to `http://localhost:4000/event` (matches `server.js`).
 * - In production, telemetry is disabled unless `VITE_LOG_ENDPOINT` is explicitly set.
 */
import { clientLogger } from '@/lib/clientLogger';

const getLogEndpoint = (): string | null => {
  const configured = import.meta.env.VITE_LOG_ENDPOINT as string | undefined;
  if (configured && configured.trim().length > 0) return configured.trim();

  return import.meta.env.DEV ? 'http://localhost:4000/event' : null;
};

export const logEvent = async (type: string, payload?: object) => {
  try {
    const endpoint = getLogEndpoint();
    if (!endpoint) return;

    // Fire and forget - don't await the response to avoid blocking UI.
    // `keepalive` helps on page unload for some browsers.
    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ type, payload }),
      keepalive: true,
    }).catch((err) => clientLogger.error('Log server unreachable:', err));
  } catch (error) {
    // Silently fail if logging fails
    clientLogger.error('Logging error:', error);
  }
};

